import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@lib/utils";

const badgeVariants = cva(
	`focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20
	dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex w-fit shrink-0 items-center
	justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium
	whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3`,
	{
		variants: {
			variant: {
				primary: "text-primary-foreground bg-primary [a&]:hover:bg-primary/90",
				default: "text-primary-foreground bg-primary [a&]:hover:bg-primary/90",
				block: "bg-block text-white [a&]:hover:bg-block/90",
				secondary: "bg-secondary text-white [a&]:hover:bg-secondary/90",
				outline: "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
				ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
				link: "text-primary underline-offset-4 [a&]:hover:underline",
				error: "border-[1.5px] border-ground bg-error text-white [a&]:hover:bg-error/90",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

/** Displays a badge or a component that looks like a badge.
 * @prop className - The class name to apply to the badge
 * @prop variant - The variant of the badge
 * @prop asChild - Whether to render the badge as a child component
 */
const Badge = ({
	className,
	variant = "default",
	asChild = false,
	...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) => {
	const Comp = asChild ? Slot : "span";

	return (
		<Comp data-slot="badge" data-variant={variant} className={cn(badgeVariants({ variant }), className)} {...props} />
	);
};

enum EBadgeVariant {
	primary = "bg-primary",
	error = "bg-error-400",
}

interface BadgeIconProps {
	variant?: "primary" | "error";
	symbol?: string | React.ReactNode;
	className?: string;
}

/** Displays a badge icon or a component that looks like a badge.
 * @prop variant - The variant of the badge
 * @prop symbol - The symbol of the badge. Can be use as child component.
 * @prop className - The class name to apply to the badge
 * @prop asChild - Whether to render the badge as a child component
 */
const BadgeIcon = ({
	variant = "primary",
	symbol,
	className,
	asChild,
}: React.ComponentProps<"div"> & BadgeIconProps & { asChild?: boolean }) => {
	const Comp = asChild ? Slot : "div";

	return (
		<Comp
			className={cn(
				"flex items-center justify-center rounded-2xl border-[1.5px] border-solid border-ground font-bold text-white",
				EBadgeVariant[variant],
				className,
			)}
		>
			{symbol}
			{/* <Typography size="xs" type="span" padding="none">{symbol}</Typography> */}
		</Comp>
	);
};

Badge.displayName = "Badge";
BadgeIcon.displayName = "BadgeIcon";

export { Badge, BadgeIcon };
